import AnimationFrame from "../graphics/AnimationFrame";
import { CostumeSet } from "./Costume";
import CostumeCompositor from "./CostumeCompositor";

class CostumeRenderer implements AnimationFrame {
  private canvas: HTMLCanvasElement;
  private costumes: CostumeSet;
  private costumeCompositor: CostumeCompositor;
  private needDraw: boolean;

  constructor(
    canvas: HTMLCanvasElement,
    costumeCompositor: CostumeCompositor,
    initialCostumes: CostumeSet
  ) {
    this.canvas = canvas;
    this.costumes = initialCostumes;
    this.costumeCompositor = costumeCompositor;
    this.needDraw = true;
  }

  public updateCostumes(costumes: CostumeSet) {
    this.costumes = costumes;
    this.needDraw = true;
  }

  public initialize() {}

  public update(elapsedTime: number) {}

  public draw(elapsedTime: number) {
    if (!this.needDraw) {
      return;
    }

    const images = this.costumeCompositor.compose(this.costumes);

    const asyncDraw = async () => {
      const bufferCanvas = this.canvas.cloneNode() as HTMLCanvasElement;

      const bufferContext = bufferCanvas.getContext("2d");
      if (!bufferContext)
        throw new Error("failed to get context from buffer canvas");

      await Promise.all(
        images.map(async (i) => {
          const image = await i.load();
          const alpha = i.alpha;
          const ret: [HTMLImageElement, number | null] = [image, alpha];
          return ret;
        })
      ).then((x) =>
        x.map(([image, alpha]) => this.drawImage(bufferContext, image, alpha))
      );

      const context = this.canvas.getContext("2d");
      if (!context) throw new Error("failed to get context from canvas");

      // Copy buffer to original canvas
      context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      context.drawImage(
        bufferCanvas,
        0,
        0,
        bufferCanvas.width,
        bufferCanvas.height
      );
    };

    asyncDraw();

    this.needDraw = false;
  }

  private drawImage(
    context: CanvasRenderingContext2D,
    image: HTMLImageElement,
    alpha: number | null
  ) {
    const height = this.canvas.height;
    const width = (height * image.width) / image.height;

    const x = (this.canvas.width - width) / 2;
    const y = (this.canvas.height - height) / 2;

    const previousAlpha = context.globalAlpha;

    if (alpha !== null) {
      context.globalAlpha = alpha;
    }

    try {
      context.drawImage(image, x, y, width, height);
    } finally {
      context.globalAlpha = previousAlpha;
    }
  }
}

export default CostumeRenderer;
